import { filterAccountNumbersByCloudProvider } from "../../../CloudProviderContext";

const asTrimmed = (value) => (value == null ? "" : String(value).trim());

export const isCustomerActive = (customer) =>
  customer?.is_active === true ||
  customer?.isActive === true ||
  customer?.active === true;

/** Sub-orgs are margin rows that were not created as FinOps customers. */
export const isSubOrganization = (customer) => customer?.is_customer !== true;

export const getCustomerDisplayName = (customer) =>
  customer?.customer_name || customer?.orgcode || "this customer";

const payerFromSource = (source, fallbackId = "") => {
  if (source == null) return null;

  if (typeof source === "string" || typeof source === "number") {
    const id = asTrimmed(source);
    return id ? { id, name: "", accountNumber: "" } : null;
  }

  if (typeof source !== "object") return null;

  const id = asTrimmed(
    source.payer_account_id ?? source.payerAccountId ?? fallbackId
  );
  if (!id) return null;

  const name = asTrimmed(
    source.payer_account_name ?? source.payerAccountName
  );
  const explicitAccount = asTrimmed(
    source.account_number ?? source.accountNumber
  );
  const accountNumber =
    explicitAccount && explicitAccount !== id ? explicitAccount : "";

  return { id, name, accountNumber };
};

/**
 * Payers from margin-summary / billing `account_payers`.
 * Accepts an id list, payer objects, or a map whose key is the linked account
 * when it differs from `payer_account_id`.
 */
export const getCustomerPayers = (customer) => {
  const raw = customer?.account_payers;
  if (raw == null) return [];

  const entries = [];

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const payer = payerFromSource(item);
      if (payer) entries.push(payer);
    });
  } else if (typeof raw === "object") {
    Object.entries(raw).forEach(([key, item]) => {
      const payer = payerFromSource(item, key);
      if (!payer) return;
      if (!payer.accountNumber && payer.id !== key) {
        payer.accountNumber = asTrimmed(key);
      }
      entries.push(payer);
    });
  } else {
    const payer = payerFromSource(raw);
    if (payer) entries.push(payer);
  }

  return entries;
};

export const getUniqueCustomerPayers = (customer) => {
  const seen = new Set();
  const unique = [];
  getCustomerPayers(customer).forEach((payer) => {
    if (!payer.id || seen.has(payer.id)) return;
    seen.add(payer.id);
    unique.push(payer);
  });
  return unique;
};

/** Per-account payer: accounts API, then the account entry, then account_payers linkage. */
export const resolveAccountPayer = (customer, accountNumber, sources = []) => {
  for (const source of sources) {
    const payer = payerFromSource(source);
    if (payer) return { id: payer.id, name: payer.name };
  }

  const normalizedAccount = asTrimmed(accountNumber);
  const linked = getCustomerPayers(customer).find(
    (payer) => payer.accountNumber && payer.accountNumber === normalizedAccount
  );
  return linked ? { id: linked.id, name: linked.name } : null;
};

const readEntryPayer = (account) => {
  if (!account || typeof account !== "object") {
    return { payerAccountId: "", payerAccountName: "", accountName: "" };
  }
  return {
    payerAccountId: asTrimmed(account.payer_account_id ?? account.payerAccountId),
    payerAccountName: asTrimmed(
      account.payer_account_name ?? account.payerAccountName
    ),
    accountName: asTrimmed(account.account_name ?? account.accountName),
  };
};

/** Account entries from margin summary customer row */
export const getCustomerAccountEntries = (customer) => {
  const rawAccountNumbers = customer?.account_numbers;

  if (Array.isArray(rawAccountNumbers)) {
    return rawAccountNumbers.map((account) => {
      const payer = readEntryPayer(account);
      return {
        accountNumber: String(
          (account && typeof account === "object"
            ? account.account_number ?? account.accountNumber
            : account) || ""
        ),
        company: String(
          (account && typeof account === "object" && account.company) ||
            customer?.customer_name ||
            ""
        ),
        status: String(
          (account && typeof account === "object" && account.status) || "SHADOW"
        ),
        ...payer,
      };
    });
  }

  if (rawAccountNumbers && typeof rawAccountNumbers === "object") {
    return Object.entries(rawAccountNumbers).map(([key, account]) => ({
      accountNumber: String(account?.account_number || key || ""),
      company: String(account?.company || customer?.customer_name || ""),
      status: String(account?.status || "SHADOW"),
      ...readEntryPayer(account),
    }));
  }

  return [];
};

export const getCustomerAccountNumbers = (customer) =>
  getCustomerAccountEntries(customer)
    .map((acc) => acc.accountNumber)
    .filter(Boolean);

export const getAwsCustomerAccountNumbers = (customer, cloudTypeLookup) =>
  filterAccountNumbersByCloudProvider(
    getCustomerAccountNumbers(customer),
    "AWS",
    cloudTypeLookup
  );

export const getAwsCustomerAccountCount = (customer, cloudTypeLookup) =>
  getAwsCustomerAccountNumbers(customer, cloudTypeLookup).length;

export const getAwsCustomerAccountEntries = (customer, cloudTypeLookup) => {
  const awsAccountNumbers = new Set(
    getAwsCustomerAccountNumbers(customer, cloudTypeLookup)
  );
  return getCustomerAccountEntries(customer).filter((acc) =>
    awsAccountNumbers.has(acc.accountNumber)
  );
};

/** Merge org-wide accounts with this customer's assigned accounts from margin data */
export const mergeOrgAccountsWithCustomer = (apiAccounts, customer) => {
  const map = new Map();

  (apiAccounts || []).forEach((acc) => {
    if (acc?.accountNumber) {
      map.set(acc.accountNumber, acc);
    }
  });

  getCustomerAccountEntries(customer).forEach((acc) => {
    if (acc.accountNumber && !map.has(acc.accountNumber)) {
      map.set(acc.accountNumber, acc);
    }
  });

  return Array.from(map.values());
};
